import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLiveRefresh } from '../hooks/useLiveRefresh.js';
import { timeAgo } from '../utils/timeAgo.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

const STATUS = {
  nieuw: { label: 'nieuw', color: '#991b1b', bg: '#fee2e2' },
  terug: { label: 'terug', color: '#9a3412', bg: '#ffedd5' },
  bij_project: { label: 'bij project', color: '#1e40af', bg: '#dbeafe' },
  wacht_op_henk: { label: 'wacht op jou', color: '#991b1b', bg: '#fee2e2' },
  opgelost: { label: 'opgelost', color: '#166534', bg: '#dcfce7' },
};

// "2026-10-24 10:15:00" (server time) → "24-10 10:15"; short enough for a phone row.
const kortMoment = (op) => {
  const [datum = '', tijd = ''] = String(op).split(' ');
  const [, maand, dag] = datum.split('-');
  return `${dag}-${maand} ${tijd.slice(0, 5)}`;
};

// Server errors of every project (HavunCore error_groups) in one list with a
// project filter (Henk 19-10-2026): what waits on Henk first, then what is being
// handled, then what was fixed with its plain-language explanation. Sessions
// close errors; Henk answers their questions here.
// Doc: HavunCore/docs/kb/reference/foutmeldingen.md §Foutmeldingen in de app
export function FoutenView({ token }) {
  const [groepen, setGroepen] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [open, setOpen] = useState(null);
  const [details, setDetails] = useState({});
  const [antwoorden, setAntwoorden] = useState({});
  const [bezig, setBezig] = useState(null);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const fetchFouten = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/meldingen/fouten`, { headers });
      setGroepen(res.data?.data || []);
      setError(null);
    } catch {
      setError('Kon de foutmeldingen niet ophalen');
    }
  }, [headers]);

  useLiveRefresh(token, fetchFouten);

  const toggle = async (id) => {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    if (details[id]) return;
    try {
      const res = await axios.get(`${API_URL}/api/meldingen/fouten/${id}`, { headers });
      setDetails((d) => ({ ...d, [id]: res.data?.data }));
    } catch {
      setDetails((d) => ({ ...d, [id]: { fout: 'Details niet op te halen' } }));
    }
  };

  const verstuur = async (id) => {
    const tekst = (antwoorden[id] || '').trim();
    if (!tekst) return;
    setBezig(id);
    try {
      await axios.post(`${API_URL}/api/meldingen/fouten/${id}/antwoord`, { antwoord: tekst }, { headers });
      setAntwoorden((a) => ({ ...a, [id]: '' }));
      await fetchFouten();
    } catch {
      setError('Antwoord niet verstuurd — probeer het opnieuw');
    } finally {
      setBezig(null);
    }
  };

  const projecten = useMemo(() => {
    const telling = {};
    (groepen || []).forEach((g) => { telling[g.project] = (telling[g.project] || 0) + 1; });
    return Object.entries(telling).sort(([a], [b]) => a.localeCompare(b));
  }, [groepen]);
  const lijst = groepen || [];

  if (!groepen && !error) {
    return <div style={styles.container}><p style={{ color: '#6b7280' }}>Laden...</p></div>;
  }

  const zichtbaar = filter === 'alle' ? lijst : lijst.filter((g) => g.project === filter);
  const wachtOpJou = zichtbaar.filter((g) => g.status === 'wacht_op_henk');
  const inBehandeling = zichtbaar.filter((g) => !['wacht_op_henk', 'opgelost'].includes(g.status));
  const opgelost = zichtbaar.filter((g) => g.status === 'opgelost');

  const kaart = (g) => {
    const st = STATUS[g.status] || STATUS.nieuw;
    const d = details[g.id];
    return (
      <div key={g.id} style={styles.card}>
        <div onClick={() => toggle(g.id)} style={{ cursor: 'pointer' }}>
          <div style={styles.cardTop}>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{g.project}</span>
            <span style={{ ...styles.badge, color: st.color, background: st.bg }}>{st.label}</span>
          </div>
          <div style={{ fontSize: '13px', marginTop: '4px', color: '#111827' }}>
            {g.exception_class?.split('\\').pop()} — <code>{g.plek}</code>
          </div>
          <div style={open === g.id ? styles.messageFull : styles.messageShort}>{g.message}</div>
          {g.status === 'opgelost' && g.uitleg && (
            <div style={styles.uitleg}>✅ {g.uitleg}</div>
          )}
          {g.antwoord && g.status !== 'opgelost' && (
            <div style={styles.meta}>Jouw antwoord: {g.antwoord}</div>
          )}
          <div style={styles.meta}>
            {g.aantal}× · laatst {timeAgo(g.laatst_gezien)}
            {g.status === 'opgelost' && g.opgelost_in && ` · ${g.opgelost_in}`}
          </div>
        </div>

        {g.status === 'wacht_op_henk' && (
          <div style={styles.vraag}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>❓ {g.vraag}</div>
            <textarea
              aria-label={`Antwoord op de vraag over ${g.project}`}
              value={antwoorden[g.id] || ''}
              onChange={(e) => setAntwoorden((a) => ({ ...a, [g.id]: e.target.value }))}
              rows={2}
              style={styles.textarea}
              placeholder="Typ je antwoord…"
            />
            <button
              onClick={() => verstuur(g.id)}
              disabled={bezig === g.id || !(antwoorden[g.id] || '').trim()}
              style={styles.sendBtn}
            >{bezig === g.id ? 'Versturen…' : 'Verstuur antwoord'}</button>
          </div>
        )}

        {open === g.id && (
          <div style={{ marginTop: '8px' }}>
            {!d && <div style={styles.meta}>Laden...</div>}
            {d?.fout && <div style={styles.errorBox}>{d.fout}</div>}
            {d && !d.fout && (
              <>
                {d.url && <div style={styles.meta}>{d.method} {d.url}</div>}
                <pre style={styles.trace}>{d.trace || '(geen trace bewaard — ouder dan 30 dagen)'}</pre>
                {d.voorvallen?.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={styles.meta}>Laatste keren</div>
                    {d.voorvallen.map((v) => (
                      <div key={`${v.op}-${v.request_id}`} style={styles.voorval}>
                        <span>{kortMoment(v.op)}</span>
                        {v.request_id && <span title={v.request_id} style={styles.verzoek}>{v.request_id.slice(0, 8)}</span>}
                        {v.activiteit_url && (
                          <a href={v.activiteit_url} target="_blank" rel="noopener noreferrer">Wat deed de klant?</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const filterKnop = (sleutel, label) => (
    <button
      key={sleutel}
      onClick={() => setFilter(sleutel)}
      aria-pressed={filter === sleutel}
      style={{ ...styles.filterBtn, ...(filter === sleutel ? styles.filterActief : {}) }}
    >{label}</button>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Foutmeldingen</h2>
        <button onClick={fetchFouten} style={styles.refreshBtn}>Vernieuwen</button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {projecten.length > 0 && (
        <div style={styles.filters}>
          {filterKnop('alle', `Alle (${lijst.length})`)}
          {projecten.map(([p, n]) => filterKnop(p, `${p} (${n})`))}
        </div>
      )}

      {wachtOpJou.length > 0 && (
        <>
          <h3 style={{ ...styles.sectionHeader, color: '#991b1b' }}>❓ Wacht op jou ({wachtOpJou.length})</h3>
          <div style={styles.cardList}>{wachtOpJou.map(kaart)}</div>
        </>
      )}

      <h3 style={styles.sectionHeader}>🔧 In behandeling ({inBehandeling.length})</h3>
      {groepen && inBehandeling.length === 0 && (
        <div style={{ ...styles.card, color: '#10b981', fontSize: '14px' }}>✓ Geen open fouten</div>
      )}
      <div style={styles.cardList}>{inBehandeling.map(kaart)}</div>

      {opgelost.length > 0 && (
        <>
          <h3 style={styles.sectionHeader}>✅ Opgelost — laatste 30 dagen ({opgelost.length})</h3>
          <div style={styles.cardList}>{opgelost.map(kaart)}</div>
        </>
      )}

      <p style={styles.footer}>
        Fouten uit de Fotusa-app staan in{' '}
        <a href="https://admin.fotusa.nl/foutmeldingen" target="_blank" rel="noopener noreferrer">admin.fotusa.nl/foutmeldingen</a>
      </p>
    </div>
  );
}

const styles = {
  container: { padding: '20px', maxWidth: '640px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { margin: 0, fontSize: '1.25rem', fontWeight: '700' },
  refreshBtn: { padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
  filterBtn: { padding: '6px 12px', borderRadius: '9999px', border: '1px solid #e5e7eb', background: 'white', fontSize: '13px', cursor: 'pointer', color: '#374151' },
  filterActief: { background: '#1d4ed8', borderColor: '#1d4ed8', color: 'white', fontWeight: 600 },
  sectionHeader: { margin: '12px 0 8px', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', fontWeight: 600 },
  cardList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  card: { background: 'white', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge: { fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '9999px' },
  messageShort: { fontSize: '12px', color: '#6b7280', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  messageFull: { fontSize: '12px', color: '#374151', marginTop: '4px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
  uitleg: { fontSize: '13px', color: '#166534', marginTop: '6px' },
  vraag: { marginTop: '10px', padding: '10px', background: '#fef2f2', borderRadius: '8px' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  sendBtn: { marginTop: '6px', padding: '8px 14px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  meta: { fontSize: '11px', color: '#9ca3af', marginTop: '4px' },
  voorval: { display: 'flex', gap: '10px', alignItems: 'baseline', fontSize: '12px', padding: '2px 0' },
  verzoek: { fontFamily: 'monospace', color: '#6b7280' },
  trace: { fontSize: '11px', background: '#f3f4f6', padding: '8px', borderRadius: '6px', overflowX: 'auto', maxHeight: '300px', whiteSpace: 'pre', marginTop: '6px' },
  footer: { fontSize: '12px', color: '#6b7280', textAlign: 'center', marginTop: '16px' },
};

export default FoutenView;
